import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@nexus/ui"
import { useTranslation } from "react-i18next"
import { useExportAction } from "../../../hooks/useExportAction.js"
import type { OpportunitiesFilters } from "../OpportunitiesTableToolbar/OpportunitiesTableToolbar.js"
import { hasActiveOpportunityFilters } from "../../../constants/opportunity-filters.js"

export interface ExportDropdownProps {
  tenantId: string
  setId: string
  selectedIds: string[]
  selectedCount: number
  /** Active tab, used when exporting filtered results. */
  tab: string
  search: string
  filters: OpportunitiesFilters
}

export function ExportDropdown({
  tenantId,
  setId,
  selectedIds,
  selectedCount,
  tab,
  search,
  filters,
}: ExportDropdownProps) {
  const { t } = useTranslation()
  const { execute, isExporting } = useExportAction()

  const filtersActive = tab !== "active" || hasActiveOpportunityFilters(filters, search)

  function handleExportSelected() {
    execute({ mode: "selected", tenantId, setId, opportunityIds: selectedIds })
  }

  function handleExportFiltered() {
    execute({ mode: "filtered", tenantId, setId, tab, search, ...filters })
  }

  function handleExportAll() {
    execute({ mode: "all", tenantId, setId })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          iconStart="download"
          disabled={isExporting}
          data-testid="export-dropdown-trigger"
        >
          {t("opportunities.export.trigger")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem
          data-export-mode="selected"
          disabled={selectedCount === 0 || isExporting}
          onSelect={handleExportSelected}
        >
          {t("opportunities.export.exportSelected", { count: selectedCount })}
        </DropdownMenuItem>

        {filtersActive && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-export-mode="filtered"
              disabled={isExporting}
              onSelect={handleExportFiltered}
            >
              {t("opportunities.export.exportFiltered")}
            </DropdownMenuItem>
          </>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem
          data-export-mode="all"
          disabled={isExporting}
          onSelect={handleExportAll}
        >
          {t("opportunities.export.exportAll")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
